import { useCallback, useEffect, useRef, useState } from 'react';
import { BufferedFrameCache, Frame, readyForPlayback } from '../caching';
import { useMapSettings } from '@app/contexts/MapSettingsContext';

function getNextFrame(
  cache: BufferedFrameCache | null,
  currentFrameIndex: number | null,
): [number | null, Frame | null] {
  if (cache == null || cache.frames.length < 1 || currentFrameIndex == null) {
    return [null, null];
  }
  if (currentFrameIndex < cache.frames.length - 1) {
    return [currentFrameIndex + 1, cache.frames[currentFrameIndex + 1]];
  }
  return [0, cache.frames[0]];
}

export function usePlaybackBuffering(cache: BufferedFrameCache | null) {
  const [state, setState] = useState<'PLAYING' | 'PAUSED'>('PAUSED');
  const [currentFrame, setCurrentFrame] = useState<number | null>(null);
  const [waitingForBuffer, setWaitingForBuffer] = useState<boolean>(false);
  const { settings: perfSettings } = useMapSettings();

  // Remember the last calculated interval to reuse when we can't calculate a new one
  const lastIntervalRef = useRef<number>(1000 / 12); // Default based on DEFAULT_MAP_SETTINGS

  // Check if the immediate next frame is ready right now
  const isNextFrameReady = useCallback(
    (currentIndex: number | null, cacheLocal: BufferedFrameCache | null): boolean | null => {
      if (currentIndex == null || cacheLocal == null || cacheLocal.frames.length < 1) {
        return null;
      }
      const [nextIndex, nextFrame] = getNextFrame(cacheLocal, currentIndex);
      if (nextIndex == null || nextFrame == null) return null;
      return readyForPlayback(nextFrame);
    },
    [],
  );

  // Check if at least the next `minBufferToResume` frames are ready right now
  const hasBufferedAhead = useCallback(
    (currentIndex: number | null, cacheLocal: BufferedFrameCache | null): boolean | null => {
      if (currentIndex == null || cacheLocal == null || cacheLocal.frames.length < 1) {
        return null;
      }
      let frameIndex = currentIndex;
      for (let i = 0; i < perfSettings.prefetchWindowSize; i += 1) {
        frameIndex += 1;
        if (frameIndex >= cacheLocal.frames.length) {
          frameIndex = 0;
        }
        const frame = cacheLocal.frames[frameIndex];
        if (!readyForPlayback(frame)) {
          return false;
        }
      }
      return true;
    },
    [perfSettings.prefetchWindowSize],
  );

  const pause = useCallback(() => {
    setState('PAUSED');
    setWaitingForBuffer(false);
  }, []);

  const play = useCallback(() => {
    setState('PLAYING');
  }, []);

  const seek = useCallback(
    (index: number | null) => {
      pause();
      setCurrentFrame(index);
    },
    [pause],
  );

  useEffect(() => {
    if (cache == null) {
      seek(null);
    }
  }, [cache, seek]);

  // Derive waitingForBuffer reactively when playing with latch semantics:
  // - Enter waiting state only if the next frame is not ready
  // - Exit waiting state only when the next `minBufferToResume` frames are ready
  useEffect(() => {
    if (state !== 'PLAYING') {
      if (waitingForBuffer) {
        setWaitingForBuffer(false);
      }
      return;
    }

    if (!waitingForBuffer) {
      const nextReady = isNextFrameReady(currentFrame, cache);
      if (nextReady === false) {
        setWaitingForBuffer(true);
      }
      return;
    }

    // waitingForBuffer is true → check if we can resume
    const canResume = hasBufferedAhead(currentFrame, cache);
    if (canResume === true) {
      setWaitingForBuffer(false);
    }
  }, [state, currentFrame, cache, waitingForBuffer, isNextFrameReady, hasBufferedAhead]);

  useEffect(() => {
    if (state === 'PAUSED' || waitingForBuffer || cache == null || currentFrame == null) {
      return;
    }

    // Calculate the time-based interval for the current frame
    const currentFrameObj = cache.frames[currentFrame];
    const [nextFrameIndex] = getNextFrame(cache, currentFrame);

    let intervalMs: number;
    // Check if we're looping back to the beginning (next frame is 0 while we're at the end)
    const isLooping = nextFrameIndex === 0 && currentFrame === cache.frames.length - 1;

    if (nextFrameIndex != null && nextFrameIndex !== currentFrame && !isLooping) {
      const nextFrameObj = cache.frames[nextFrameIndex];

      // Calculate time delta between frames in milliseconds
      const timeDeltaMs = Math.abs(
        nextFrameObj.datetime.getTime() - currentFrameObj.datetime.getTime(),
      );

      // animationSpeed represents how many real-time seconds to show per hour of forecast time
      // For example: animationSpeed=1 means 1 hour of forecast plays in 1 second
      // timeDeltaMs is in milliseconds of forecast time
      // We want to convert this to playback milliseconds
      const forecastHours = timeDeltaMs / (1000 * 60 * 60);
      intervalMs = (forecastHours / perfSettings.animationSpeed) * 1000;

      // Clamp to reasonable bounds (at least 16ms for ~60fps, at most 10s)
      intervalMs = Math.max(16, Math.min(10000, intervalMs));

      // Store this interval for future reuse
      lastIntervalRef.current = intervalMs;
    } else {
      // Reuse the last calculated interval when looping or when we can't calculate a new one
      intervalMs = lastIntervalRef.current;
    }

    const timeout = setTimeout(() => {
      setCurrentFrame((prev) => {
        const [nextFrameIndex, nextFrame] = getNextFrame(cache, prev);
        if (nextFrameIndex == null || nextFrameIndex === prev || nextFrame == null) {
          return prev;
        }

        if (!readyForPlayback(nextFrame)) {
          // Not 100% sure if this is actually possible, but just in case there's a race condition here
          // let's not try to render unrenderable frames during playback.
          return prev;
        }

        return nextFrameIndex;
      });
    }, intervalMs);

    return () => {
      clearTimeout(timeout);
    };
  }, [cache, state, currentFrame, waitingForBuffer, perfSettings.animationSpeed]);

  return {
    state,
    currentFrame,
    waitingForBuffer,
    play,
    pause,
    seek,
  };
}
